import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { getRecaptchaToken } from '../lib/recaptcha';
import { CheckCircle2, Dumbbell, Palette, Sprout, Cpu } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AREAS = [
  { icon: Dumbbell, name: 'Athletics', text: 'Move. Climb. Balance. Coordinate. Get stronger. Work together. Try the thing that feels hard. Develop confidence in what your body can do.' },
  { icon: Palette, name: 'Arts', text: "Make things. Design things. Tell stories. Build something that didn't exist before. Experiment without needing there to be one right answer." },
  { icon: Sprout, name: 'Agriculture & Nature', text: "Grow something. Get dirty. Understand where things come from. Observe natural systems. Take responsibility for something living and discover that nature doesn't operate on demand." },
  { icon: Cpu, name: 'AI + Technology', text: 'Learn to use extraordinarily powerful tools without becoming dependent on them. Ask better questions. Create. Investigate. Solve problems. Learn when AI can expand your capabilities — and when you should figure it out yourself.' },
];

const OUTCOME_OPTIONS = ['Resilience', 'Independence', 'Confidence', 'Physical capability', 'Creativity', 'Tech literacy', 'Social skills'];
const AREA_OPTIONS = ['Athletics', 'Arts', 'Agriculture / Nature', 'AI + Technology'];

const initialForm = {
  parent_first_name: '', parent_last_name: '', email: '', phone: '', children_grades: '', schooling: '', school_name: '',
  option_interest: '', days_hours: '', frequency: '', pickup_window: '', transportation: '',
  current_enrichment: '', frustrations: '', outcomes: [], areas: [],
  autonomy_comfort: '', budget: '', change_outside_school: '', change_inside_school: '',
  contact_pilot: ''
};

export default function YouthPage() {
  const [form, setForm] = useState(initialForm);
  const [honeypot, setHoneypot] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const mountedAt = useRef(Date.now());

  useEffect(() => { mountedAt.current = Date.now(); }, []);

  const set = (name) => (e) => setForm(prev => ({ ...prev, [name]: e.target.value }));

  const toggle = (name, value) => () => setForm(prev => ({
    ...prev,
    [name]: prev[name].includes(value) ? prev[name].filter(v => v !== value) : [...prev[name], value]
  }));

  const scrollToSurvey = () => document.getElementById('youth-survey')?.scrollIntoView({ behavior: 'smooth' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.parent_first_name.trim() || !form.parent_last_name.trim() || !form.email.trim()) {
      setError('Please add your first name, last name and email so we can follow up.');
      return;
    }
    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken('youth_survey');
      await axios.post(`${BACKEND_URL}/api/youth/survey`, {
        ...form,
        email: form.email.toLowerCase().trim(),
        website: honeypot,
        recaptcha_token: recaptchaToken,
        form_ms: Date.now() - mountedAt.current
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong — please try again in a few minutes.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="youth-page" data-testid="youth-page">
      {/* HERO */}
      <section className="youth-hero">
        <span className="youth-eyebrow">A Developing Youth Initiative</span>
        <h1 className="youth-headline">How do we teach our kids resilience?<br /><em>Easy. We don't.</em></h1>
        <p className="youth-lede-strong">We create the environment for them to learn it themselves.</p>
        <p className="youth-lede">
          An environment that challenges traditional conditioning. That sparks interest, encourages play and generates
          curiosity. A place where kids have room to try something, struggle with it, get it wrong, change direction,
          ask questions, succeed — and discover what they're capable of.
        </p>
      </section>

      {/* PHILOSOPHY */}
      <section className="youth-philosophy" data-testid="youth-philosophy">
        <p>
          Adults provide the environment, the resources, the challenges, the safety and the guidance.
          <strong> But we don't do all the thinking for them.</strong> Because in a world where answers, entertainment
          and assistance are available almost instantly, the ability to learn on your own may be one of the most
          important skills we can help our kids preserve.
        </p>
        <p className="youth-philosophy-tag">That's the initiative we're developing.</p>
      </section>

      {/* CTA */}
      <section className="youth-hero youth-hero-cta-block">
        <Button className="youth-cta" onClick={scrollToSurvey} data-testid="youth-hero-cta">
          Help Us Build It → Take the Parent Survey
        </Button>
        <p className="youth-cta-note">
          We're exploring daytime options for homeschool families and after-school programming for elementary-age kids.
          If you're looking for something different for your child, we want to hear from you.
        </p>
      </section>

      {/* FOUR AREAS */}
      <section className="youth-areas-section">
        <h2 className="youth-section-title">The environments we're exploring</h2>
        <p className="youth-section-sub">We're building around four areas that give kids very different ways to encounter challenge, curiosity, responsibility and discovery.</p>
        <div className="youth-areas-grid" data-testid="youth-areas-grid">
          {AREAS.map(({ icon: Icon, name, text }) => (
            <div className="youth-area-card" key={name}>
              <Icon size={26} className="youth-area-icon" />
              <h3>{name}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT IT MIGHT LOOK LIKE */}
      <section className="youth-shape" data-testid="youth-shape">
        <h2 className="youth-section-title">What does this actually look like?</h2>
        <p><strong>We're still developing it — intentionally.</strong></p>
        <p>
          We're exploring small groups, potentially including a <strong>daytime program for homeschool families</strong> and
          an <strong>after-school option for elementary-age kids</strong>. We don't have every detail decided yet because
          we want to hear from the families who might actually use it.
        </p>
        <p>
          What do your kids need? What do <em>you</em> need? What's missing from what's currently available?
        </p>
        <Button className="youth-cta" onClick={scrollToSurvey} data-testid="youth-shape-cta">
          Take the Parent Survey →
        </Button>
      </section>

      {/* SURVEY */}
      <section className="youth-survey-section" id="youth-survey">
        <div className="youth-survey-card">
          {success ? (
            <div className="youth-success" data-testid="youth-survey-success">
              <CheckCircle2 size={44} />
              <h2>Thank you — you're helping build this.</h2>
              <p>We'll reach out as the concept takes shape. Your answers directly influence what we create.</p>
            </div>
          ) : (
            <>
              <h2 className="youth-section-title">The Parent Survey</h2>
              <p className="youth-section-sub">5 minutes. Every field beyond name + email is optional — answer what you can.</p>
              <form onSubmit={handleSubmit} className="youth-form" data-testid="youth-survey-form">
                <input type="text" name="website" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                  className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                <fieldset>
                  <legend>About your family</legend>
                  <div className="youth-form-row">
                    <label>First Name *
                      <input type="text" value={form.parent_first_name} onChange={set('parent_first_name')} required data-testid="youth-first-name" />
                    </label>
                    <label>Last Name *
                      <input type="text" value={form.parent_last_name} onChange={set('parent_last_name')} required data-testid="youth-last-name" />
                    </label>
                  </div>
                  <div className="youth-form-row">
                    <label>Email *
                      <input type="email" value={form.email} onChange={set('email')} required data-testid="youth-email" />
                    </label>
                    <label>Phone (optional)
                      <input type="tel" value={form.phone} onChange={set('phone')} data-testid="youth-phone" />
                    </label>
                  </div>
                  <div className="youth-form-row">
                    <label>Grade(s) of your children
                      <input type="text" placeholder="e.g. K and 3rd" value={form.children_grades} onChange={set('children_grades')} data-testid="youth-children-grades" />
                    </label>
                    <label>Where does your kid(s) go to school?
                      <input type="text" placeholder="School name (or homeschool co-op)" value={form.school_name} onChange={set('school_name')} data-testid="youth-school-name" />
                    </label>
                  </div>
                  <div className="youth-form-row">
                    <label>How are your kids schooled?
                      <select value={form.schooling} onChange={set('schooling')} data-testid="youth-schooling">
                        <option value="">Select…</option>
                        <option>Homeschool</option>
                        <option>Traditional public school</option>
                        <option>Private school</option>
                        <option>Mix / other</option>
                      </select>
                    </label>
                    <label>Which option interests you?
                      <select value={form.option_interest} onChange={set('option_interest')} data-testid="youth-option-interest">
                        <option value="">Select…</option>
                        <option>Daytime (homeschool families)</option>
                        <option>After-school (elementary age)</option>
                        <option>Both</option>
                        <option>Not sure yet</option>
                      </select>
                    </label>
                  </div>
                </fieldset>

                <fieldset>
                  <legend>What you need</legend>
                  <div className="youth-form-row">
                    <label>Days & hours you'd need
                      <input type="text" placeholder="e.g. Mon/Wed/Fri, 3–6pm" value={form.days_hours} onChange={set('days_hours')} data-testid="youth-days-hours" />
                    </label>
                    <label>Ideal frequency
                      <select value={form.frequency} onChange={set('frequency')} data-testid="youth-frequency">
                        <option value="">Select…</option>
                        <option>1–2 days / week</option>
                        <option>3 days / week</option>
                        <option>4–5 days / week</option>
                        <option>Not sure</option>
                      </select>
                    </label>
                  </div>
                  <div className="youth-form-row">
                    <label>Ideal pickup window (after-school)
                      <input type="text" placeholder="e.g. by 5:30pm" value={form.pickup_window} onChange={set('pickup_window')} data-testid="youth-pickup-window" />
                    </label>
                    <label>Would you need transportation?
                      <select value={form.transportation} onChange={set('transportation')} data-testid="youth-transportation">
                        <option value="">Select…</option>
                        <option>Yes</option>
                        <option>No</option>
                        <option>Maybe / depends</option>
                      </select>
                    </label>
                  </div>
                  <div className="youth-form-row">
                    <label>What would you realistically pay?
                      <select value={form.budget} onChange={set('budget')} data-testid="youth-budget">
                        <option value="">Select…</option>
                        <option>$395 – $500 / month</option>
                        <option>$500 – $650 / month</option>
                        <option>$650+ / month</option>
                        <option>Depends on the program</option>
                      </select>
                    </label>
                  </div>
                  <label>What do you currently use for after-school or homeschool enrichment?
                    <input type="text" value={form.current_enrichment} onChange={set('current_enrichment')} data-testid="youth-current-enrichment" />
                  </label>
                  <label>What frustrates you about the current options?
                    <textarea rows={2} value={form.frustrations} onChange={set('frustrations')} data-testid="youth-frustrations" />
                  </label>
                </fieldset>

                <fieldset>
                  <legend>What matters to you</legend>
                  <span className="youth-check-label">Which outcomes matter most? (pick any)</span>
                  <div className="youth-check-grid" data-testid="youth-outcomes">
                    {OUTCOME_OPTIONS.map(o => (
                      <label key={o} className={`youth-chip ${form.outcomes.includes(o) ? 'active' : ''}`}>
                        <input type="checkbox" checked={form.outcomes.includes(o)} onChange={toggle('outcomes', o)} />
                        {o}
                      </label>
                    ))}
                  </div>
                  <span className="youth-check-label">Which areas interest your family? (pick any)</span>
                  <div className="youth-check-grid" data-testid="youth-areas-picker">
                    {AREA_OPTIONS.map(a => (
                      <label key={a} className={`youth-chip ${form.areas.includes(a) ? 'active' : ''}`}>
                        <input type="checkbox" checked={form.areas.includes(a)} onChange={toggle('areas', a)} />
                        {a}
                      </label>
                    ))}
                  </div>
                  <label>How do you feel about intentionally less adult-directed learning?
                    <select value={form.autonomy_comfort} onChange={set('autonomy_comfort')} data-testid="youth-autonomy-comfort">
                      <option value="">Select…</option>
                      <option>Very comfortable — that's exactly what I want</option>
                      <option>Curious, but unsure</option>
                      <option>I'd prefer more structure</option>
                    </select>
                  </label>
                </fieldset>

                <fieldset>
                  <legend>The big questions</legend>
                  <label>If you could change one thing about the way your child spends the hours <strong>outside</strong> of school, what would it be?
                    <textarea rows={3} value={form.change_outside_school} onChange={set('change_outside_school')} data-testid="youth-change-outside" />
                  </label>
                  <label>And <strong>inside</strong> of school?
                    <textarea rows={3} value={form.change_inside_school} onChange={set('change_inside_school')} data-testid="youth-change-inside" />
                  </label>
                  <label>Want to be contacted about a pilot?
                    <select value={form.contact_pilot} onChange={set('contact_pilot')} data-testid="youth-contact-pilot">
                      <option value="">Select…</option>
                      <option>Yes — count us in</option>
                      <option>Maybe — keep me posted</option>
                      <option>No, just sharing feedback</option>
                    </select>
                  </label>
                </fieldset>

                {error && <p className="youth-form-error" data-testid="youth-form-error">{error}</p>}
                <Button type="submit" className="youth-cta" disabled={loading} data-testid="youth-survey-submit">
                  {loading ? 'Sending…' : 'Send My Answers'}
                </Button>
                <p className="youth-privacy">We respect your privacy. Your answers shape the program — nothing else.</p>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
